import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, useRef, useLayoutEffect } from 'react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx } from '@emotion/react';
import { devWarning, utilStyles, throttle, KeyboardCode } from '@paypalcorp/pp-react-utils';
import { textStyles } from '@paypalcorp/pp-react-text';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
      sysColorBackgroundAlt = tokens.sysColorBackgroundAlt,
      sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysColorPrimaryContrast = tokens.sysColorPrimaryContrast,
      sysColorPrimaryMain = tokens.sysColorPrimaryMain,
      sysColorStructureBorderHighContrast = tokens.sysColorStructureBorderHighContrast,
      sysMotionDurationMoveSmall = tokens.sysMotionDurationMoveSmall,
      sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysColorStructureFocus = tokens.sysColorStructureFocus,
      sysSpaceSm = tokens.sysSpaceSm,
      sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis;

  var _utilStyles = utilStyles(tokens),
      focusStyles = _utilStyles.focusStyles,
      variables = _utilStyles.variables;

  var _textStyles = textStyles(tokens),
      textCaption = _textStyles.caption;
  /* istanbul ignore next */


  var borderRadius = sysBorderRadiusInteractive === '50%' ? '64rem' : sysBorderRadiusInteractive;
  var webkitSize = '0.0421875rem';
  var segmentedControllerSpace = '0.1875rem'; // 3px

  var tabWrapBaseStyles = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: segmented_controller_tab_wrap_base;\n    background-color: ", ";\n    border-radius: ", ";\n    display: flex;\n    align-items: stretch;\n    justify-content: flex-start;\n    position: relative;\n    padding: ", ";\n    gap: ", ";\n  "])), sysColorBackgroundAlt, borderRadius, segmentedControllerSpace, segmentedControllerSpace);
  var tabStyles = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: segmented_controller_tab;\n    border-radius: inherit;\n    position: relative;\n    background-color: transparent;\n    width: 100%;\n    border: 0;\n    min-width: 0;\n    padding: 0;\n    cursor: pointer;\n    ", "\n    z-index: 1;\n\n    &:focus {\n      ", ";\n    }\n\n    @media (hover: hover) {\n      &:hover {\n        &:not(:focus) {\n          span {\n            outline: ", " solid\n              ", ";\n          }\n        }\n      }\n    }\n\n    &[aria-selected='true'] {\n      label: tab_aria_selected;\n      ", "\n      -webkit-text-stroke-width: ", ";\n\n      span {\n        color: ", ";\n      }\n      &:focus {\n        span {\n          outline-offset: -", ";\n          outline: ", " solid\n            ", ";\n        }\n      }\n    }\n  "])), textCaption(), focusStyles.focusOffsetElement, sysBorderThicknessEmphasis, sysColorStructureFocus, textCaption(), webkitSize, sysColorPrimaryMain, sysBorderThicknessNormal, sysBorderThicknessNormal, sysColorStructureBorderHighContrast);
  var tabSlider = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: slider;\n    position: absolute;\n    top: 0;\n    left: 0;\n    width: 0;\n    height: 0;\n    background: ", ";\n    border-radius: ", ";\n    -webkit-text-stroke-width: ", ";\n    transition: all ", " ", ";\n  "])), sysColorPrimaryContrast, borderRadius, webkitSize, sysMotionDurationMoveSmall, variables.ppuiEaseMoveSmall);
  var tabTitleStyles = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: tab_title_base;\n    display: flex;\n    flex-wrap: wrap;\n    justify-content: center;\n    align-items: center;\n    height: 100%;\n    word-break: break-word;\n    border: ", " solid transparent;\n    padding: ", ";\n    border-radius: inherit;\n    color: ", ";\n    pointer-events: none;\n  "])), sysBorderThicknessNormal, sysSpaceSm, sysColorBackgroundMediumContrast);
  var tabContentStyles = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: tab_content;\n\n    &[aria-hidden='true'] {\n      display: none;\n    }\n  "])));
  var tabContentVisible = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: tab_content_visible;\n    display: block;\n  "])));
  var tabContentHidden = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: tab_content_hidden;\n    display: none;\n  "])));
  return {
    tabWrapBaseStyles: tabWrapBaseStyles,
    tabStyles: tabStyles,
    tabSlider: tabSlider,
    tabTitleStyles: tabTitleStyles,
    tabContentStyles: tabContentStyles,
    tabContentVisible: tabContentVisible,
    tabContentHidden: tabContentHidden
  };
}

var _excluded = ["id", "tabIdx", "label", "segmentedControllerId", "panelId", "selected", "onClick", "className"];
var SegmentedControllerTab = React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
      tabIdx = _ref.tabIdx,
      label = _ref.label,
      segmentedControllerId = _ref.segmentedControllerId,
      panelId = _ref.panelId,
      selected = _ref.selected,
      onClick = _ref.onClick,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var focusable = selected ? 0 : -1;
  var tabId = id || "".concat(segmentedControllerId, "-tab").concat(parseInt(tabIdx, 10) + 1);
  var tabStyles = [_styles.tabStyles];
  return jsx("button", _extends({
    "data-tab-index": tabIdx,
    id: tabId,
    css: tabStyles,
    className: className,
    "aria-controls": panelId,
    "aria-selected": selected,
    tabIndex: focusable,
    type: "button",
    role: "tab",
    ref: ref
  }, rest, {
    onClick: onClick,
    "data-ppui": "true"
  }), jsx("span", {
    css: _styles.tabTitleStyles,
    "data-ppui": "true"
  }, label));
});

var _excluded$1 = ["id", "panelId", "label"];

var getRefCurrent = function (myRef) {
  return (
    /* istanbul ignore next */
    myRef && myRef.current ? myRef.current : null
  );
};

var SegmentedControllerTabs = function (_ref) {
  var _ref$name = _ref.name,
      name = _ref$name === void 0 ? 'segmentedController' : _ref$name,
      selectedTabIdx = _ref.selectedTabIdx,
      _ref$tabs = _ref.tabs,
      tabs = _ref$tabs === void 0 ? [] : _ref$tabs,
      onClick = _ref.onClick,
      className = _ref.className,
      tabClassName = _ref.tabClassName,
      tabsWrapperClassName = _ref.tabsWrapperClassName,
      keepAlive = _ref.keepAlive,
      lazyLoad = _ref.lazyLoad,
      children = _ref.children;

  /* istanbul ignore next */
  if (!onClick) {
    devWarning('Required prop `onClick` is missing');
  }

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var containerEleRef = useRef();
  var prevSelectedTabIdxRef = useRef(0);
  var isTabsFocusedRef = useRef(false);
  var focusedIdxRef = useRef();
  var sliderRef = useRef();
  var tabRef = useRef([]);

  var getSelectedTabIdx = function () {
    var index = Math.floor(selectedTabIdx);

    if (!selectedTabIdx || !index || index >= tabs.length || index < 0) {
      return 0;
    }

    return index;
  };

  var getWrapperCurrent = function (w) {
    return (
      /* istanbul ignore next */
      w && w.current ? w.current : null
    );
  }; // apply styles to the slider element based on the
  // dimention of the tab


  var applySliderStyles = function (element) {
    var _element$offsetParent;

    var slider = getWrapperCurrent(sliderRef);
    slider.style.left = "".concat((element === null || element === void 0 ? void 0 : element.getBoundingClientRect().left) - (element === null || element === void 0 ? void 0 : (_element$offsetParent = element.offsetParent) === null || _element$offsetParent === void 0 ? void 0 : _element$offsetParent.getBoundingClientRect().left), "px");
    slider.style.width = "".concat(element === null || element === void 0 ? void 0 : element.getBoundingClientRect().width, "px");
    slider.style.top = "".concat(element === null || element === void 0 ? void 0 : element.offsetTop, "px");
    slider.style.height = "".concat(element === null || element === void 0 ? void 0 : element.offsetHeight, "px");
  };

  var offsetFocus = function (offset) {
    var container = getRefCurrent(containerEleRef);
    var tabDomItems = container.querySelectorAll('[role="tab"]');
    var nextIndex = focusedIdxRef.current + offset;
    var index;
    /* istanbul ignore next */

    if (nextIndex === tabs.length) {
      index = 0;
    } else if (nextIndex === -1) {
      index = tabs.length - 1;
    } else {
      index = nextIndex;
    }

    tabDomItems[index].focus();
    focusedIdxRef.current = index;
  };

  var keyHandler = function (event) {
    if (!isTabsFocusedRef.current) {
      return;
    }

    var selectedIndex = getSelectedTabIdx();
    /* istanbul ignore next */

    var KeyCode = new KeyboardCode(event.key || event.keyCode);

    if (KeyCode.IS_ARROW_LEFT) {
      offsetFocus(-1);
    } else if (KeyCode.IS_ARROW_RIGHT) {
      offsetFocus(1);
    } else if (KeyCode.IS_ENTER || KeyCode.IS_SPACEBAR) {
      /* istanbul ignore next */
      if (selectedIndex !== focusedIdxRef.current) {
        onClick(event, focusedIdxRef.current);
      }
    }
  };

  useLayoutEffect(function () {
    prevSelectedTabIdxRef.current = getSelectedTabIdx();
  }, [tabs, selectedTabIdx, onClick]);
  useLayoutEffect(function () {
    // For the first time during initialisation
    focusedIdxRef.current = getSelectedTabIdx();
    /* istanbul ignore next */

    var resizeObserver = window.ResizeObserver ? new ResizeObserver(throttle(function () {
      applySliderStyles(tabRef.current[focusedIdxRef.current]);
    }, 100)) : null;
    /* istanbul ignore next */

    if (window.ResizeObserver) {
      resizeObserver.observe(containerEleRef.current);
    }

    return function () {
      /* istanbul ignore next */
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [selectedTabIdx]);

  var handleClick = function (event) {
    var tabIdxClicked = parseInt(event.currentTarget.getAttribute('data-tab-index'), 10);
    event.currentTarget.focus(); // This is tested, but coverage not registering it

    /* istanbul ignore next */

    if (!isNaN(tabIdxClicked)) {
      focusedIdxRef.current = tabIdxClicked;
    }

    applySliderStyles(event.target);
    onClick(event, tabIdxClicked);
  };

  var selectedIndex = getSelectedTabIdx();
  var selectedId;
  var tabItems = tabs.map(function (tabProps, index) {
    var id = tabProps.id,
        panelId = tabProps.panelId,
        label = tabProps.label,
        rest = _objectWithoutProperties(tabProps, _excluded$1);

    var keyIdentifier = "".concat(name, "-tab").concat(index + 1); // update animation (only on the selected tab)

    if (index === selectedIndex) {
      selectedId = id || keyIdentifier;
    }

    return jsx(SegmentedControllerTab, _extends({
      key: keyIdentifier,
      id: id,
      tabIdx: index.toString(),
      label: label,
      ref: function ref(el) {
        return tabRef.current[index] = el;
      },
      segmentedControllerId: name,
      panelId: panelId,
      selected: index === selectedIndex,
      onClick: handleClick,
      className: tabClassName
    }, rest, {
      "data-ppui": "true"
    }));
  });
  var tabContent = null;

  if (keepAlive || lazyLoad) {
    tabContent = React.Children.toArray(children).map(function (_panel, index) {
      var isSelected = selectedIndex === index;
      return React.cloneElement(_panel, {
        hidden: !isSelected,
        tabId: isSelected ? selectedId : undefined,
        visible: keepAlive ? true : isSelected
      });
    });
  } else {
    var tabPanel = React.Children.toArray(children).filter(function (_panel, index) {
      return selectedIndex === index;
    })[0];
    tabContent = React.cloneElement(tabPanel, {
      tabId: selectedId,
      hidden: false
    });
  }

  return jsx("div", {
    ref: containerEleRef,
    id: name,
    className: className,
    "data-ppui-info": "segmented-controller_2.10.0"
  }, jsx("div", {
    id: "".concat(name, "-tabs"),
    css: _styles.tabWrapBaseStyles,
    className: tabsWrapperClassName,
    role: "tablist",
    onFocus: function handleFocus() {
      isTabsFocusedRef.current = true;
    },
    onBlur: function handleFocusOut() {
      isTabsFocusedRef.current = false;
    },
    onKeyDown: function onKeyDown(evt) {
      return keyHandler(evt);
    },
    "data-ppui": "true"
  }, tabItems, jsx("div", {
    css: _styles.tabSlider,
    ref: sliderRef,
    "data-ppui": "true"
  })), tabContent);
};

var _excluded$2 = ["id", "tabId", "className", "hidden", "children", "visible"];

var SegmentedControllerPanel = function (_ref) {
  var id = _ref.id,
      tabId = _ref.tabId,
      className = _ref.className,
      hidden = _ref.hidden,
      children = _ref.children,
      _ref$visible = _ref.visible,
      visible = _ref$visible === void 0 ? true : _ref$visible,
      rest = _objectWithoutProperties(_ref, _excluded$2);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]); // MutableRefObject<boolean> variable to determine whether to render the component or not.


  var hasRendered = useRef(visible);
  /* istanbul ignore next */

  if (visible && !hasRendered.current) {
    hasRendered.current = true;
  }
  /* istanbul ignore next */


  if (!hasRendered.current) {
    // Do not re-render the Panel when its already present in the DOM.
    return null;
  }

  var panelContentStyles = [_styles.tabContentStyles,
  /* istanbul ignore next */
  visible ? _styles.tabContentVisible : _styles.tabContentHidden];
  return jsx("div", _extends({
    id: id,
    css: panelContentStyles,
    className: className,
    "aria-labelledby": tabId,
    "aria-hidden": hidden,
    tabIndex: 0,
    role: "tabpanel"
  }, rest, {
    "data-ppui": "true"
  }), children);
};

export { SegmentedControllerTabs as SegmentedController, SegmentedControllerPanel, SegmentedControllerTab, styles as segmentedControllerStyles };
